import type { TranslationKey } from "@privacy/translations";
import type { ComponentType } from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { Drawer } from "../../../../shared/Drawer";
import {
  ConnectedTenantEntry,
  ConnectedTenantsList,
  TenantControllerName,
  connectedTenantsText,
} from "../../../../shared/ConnectedTenantsList";
import { useCurrentCustomer } from "../../../../root/CurrentOrganisationProvider";
import {
  DpiaIcon2,
  RegistrationControllerIcon2,
  RegistrationProcessorIcon2,
  RegistrationControllerIcon1,
  RegistrationProcessorIcon1,
  DpiaIcon1,
  SystemIcon1,
  SystemIcon2,
  type IconWithBackgroundProps,
} from "../../../../shared/icons";
import { usePrivacyNavigate } from "../../../../shared/usePrivacyNavigate";
import { type GridViewUser } from "../../../../api/users";
import type { ResponsibleEntitiesKey } from "../constants";
import { ChangeResponsibleDialog } from "./ChangeResponsibleDialog";

const NAME_COLUMN_NAME = "name";
const TENANT_COLUMN_NAME = "tenants";

export const ResponsibleEntitesSubdrawer = ({
  user,
  responsibleEntitiesKey,
}: {
  user: GridViewUser;
  responsibleEntitiesKey: ResponsibleEntitiesKey;
}) => {
  const { navigate } = usePrivacyNavigate();
  const { t } = useTranslation();
  const entityProps = getEntityProps[responsibleEntitiesKey](user);

  const rows = user[responsibleEntitiesKey];
  const currentCustomer = useCurrentCustomer();

  return (
    <>
      <Drawer.SubDrawerHeader
        onBackClick={() => navigate("..")}
        title={t(entityProps.subdDrawerHeader.titleKey)}
        renderIcon={props => <entityProps.subdDrawerHeader.Icon {...props} />}
        countAsterisk={false}
        count={entityProps.subdDrawerHeader.count}
      />
      <Drawer.SelectableGrid
        initialState={{ sorting: { sortModel: [{ field: TENANT_COLUMN_NAME, sort: "asc" }] } }}
        columnVisibilityModel={{ [TENANT_COLUMN_NAME]: responsibleEntitiesKey !== "systemsWhereResponsible" }}
        getRowId={row => row.id}
        rows={rows}
        noRowsOverlay={entityProps.grid.noRowsOverlay}
        selectionAction={{
          label: t("User.Grid.Drawer.ChangeResponsibleDialog.ChangeSelected"),
          renderDialog: ({ selectedIds, onClose, onSuccess }) => (
            <ChangeResponsibleDialog
              currentResponsibleUserId={user.userId}
              onClose={onClose}
              onReplaced={onSuccess}
              selectedEntityIds={selectedIds}
              responsibleEntitiesKey={responsibleEntitiesKey}
            />
          ),
        }}
        columns={[
          {
            field: NAME_COLUMN_NAME,
            flex: 1,
            headerName: t("User.Grid.Drawer.NameHeader"),
            valueGetter: (_, { name }) => name,
            renderCell: ({ row }) => (
              <Drawer.GridCell align="center">
                <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
                  <entityProps.grid.Icon size="sm" />
                  <Stack>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "600", lineHeight: 1.2, overflowWrap: "anywhere", hyphens: "auto" }}
                    >
                      {row.name}
                    </Typography>
                  </Stack>
                </Stack>
              </Drawer.GridCell>
            ),
          },
          {
            field: TENANT_COLUMN_NAME,
            flex: 1,
            headerName: t("User.Grid.Drawer.TenantsHeader"),
            valueGetter: (_, { tenants }) => connectedTenantsText(tenants),
            renderCell: ({ row }) => (
              <Drawer.GridCell align="center">
                <ConnectedTenantsList>
                  {row.tenants.map(tenant => (
                    <ConnectedTenantEntry key={tenant.id} tenant={tenant}>
                      {currentCustomer.customerKind !== "enterprise" &&
                        responsibleEntitiesKey !== "processorRegistrationsWhereResponsible" &&
                        tenant.personalDataController && (
                          <TenantControllerName>{tenant.personalDataController.name}</TenantControllerName>
                        )}
                    </ConnectedTenantEntry>
                  ))}
                </ConnectedTenantsList>
              </Drawer.GridCell>
            ),
          },
        ]}
      />
    </>
  );
};

const RegistrationsNoRowsOverlay = () => <Drawer.GridNoRowsOverlay labelKey="RegistrationGrid.NoRows" />;

const ProcessorRegistrationsNoRowsOverlay = () => <Drawer.GridNoRowsOverlay labelKey="RegistrationGrid.NoRows" />;

const DpiasNoRowsOverlay = () => <Drawer.GridNoRowsOverlay labelKey="Dpia.Grid.NoRows" />;

const SystemsNoRowsOverlay = () => <Drawer.GridNoRowsOverlay labelKey="SystemGrid.NoRows" />;

type EntityProps = {
  subdDrawerHeader: { titleKey: TranslationKey; count: number; Icon: ComponentType<IconWithBackgroundProps> };
  grid: { Icon: ComponentType<IconWithBackgroundProps>; noRowsOverlay: ComponentType };
};

const getEntityProps: Record<ResponsibleEntitiesKey, (user: GridViewUser) => EntityProps> = {
  registrationsWhereResponsible: user => ({
    subdDrawerHeader: {
      titleKey: "User.Grid.RegistrationsWhereResponsibleHeader",
      count: user.registrationsWhereResponsible.length,
      Icon: RegistrationControllerIcon1,
    },
    grid: { Icon: RegistrationControllerIcon2, noRowsOverlay: RegistrationsNoRowsOverlay },
  }),
  processorRegistrationsWhereResponsible: user => ({
    subdDrawerHeader: {
      titleKey: "User.Grid.ProcessorRegistrationsWhereResponsibleHeader",
      count: user.processorRegistrationsWhereResponsible.length,
      Icon: RegistrationProcessorIcon1,
    },
    grid: { Icon: RegistrationProcessorIcon2, noRowsOverlay: ProcessorRegistrationsNoRowsOverlay },
  }),
  dpiasWhereResponsible: user => ({
    subdDrawerHeader: {
      titleKey: "User.Grid.DPIAsWhereResponsibleHeader",
      count: user.dpiasWhereResponsible.length,
      Icon: DpiaIcon1,
    },
    grid: { Icon: DpiaIcon2, noRowsOverlay: DpiasNoRowsOverlay },
  }),
  systemsWhereResponsible: user => ({
    subdDrawerHeader: {
      titleKey: "User.Grid.SystemsWhereResponsibleHeader",
      count: user.systemsWhereResponsible.length,
      Icon: SystemIcon1,
    },
    grid: { Icon: SystemIcon2, noRowsOverlay: SystemsNoRowsOverlay },
  }),
};
